import type { BreadcrumbProps as AntBreadcrumbProps } from 'antd';
import { Breadcrumb as AntBreadcrumb } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { Typography } from '../../Typography';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbsProps
  extends Omit<
    AntBreadcrumbProps,
    'items' | 'children' | 'itemRender' | 'routes'
  > {
  /**
   * Ordered from the top-level ancestor to the current page. The last item
   * always renders as the current, non-interactive page. Recommended range
   * is 2-5 items, matching Dime's Items variant.
   */
  items: BreadcrumbItem[];
  className?: string;
  'data-testid'?: string;
}

const StyledBreadcrumb = styled(AntBreadcrumb)`
  ol {
    align-items: center;
  }

  .ant-breadcrumb-separator {
    color: ${theme.dime.colors.text.secondary};
    margin-inline: ${theme.dime.spacing.m};
  }

  .crumb-label {
    line-height: ${theme.dime.fonts.lineHeights.md};
  }

  .ant-breadcrumb-link {
    cursor: pointer;
    padding: 0;
    margin-inline: 0;
    height: auto;
    display: inline;
    border-radius: 0;

    &:hover {
      background-color: transparent;
    }

    &:hover .crumb-label {
      color: ${theme.dime.colors.text.primary};
    }
  }

  .crumb-current {
    cursor: default;
    pointer-events: none;
  }
`;

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  className = '',
  'data-testid': testId,
  ...antProps
}) => {
  const lastIndex = items.length - 1;

  const antItems = items.map((item, index) => {
    const isCurrent = index === lastIndex;

    if (isCurrent) {
      return {
        key: `${item.label}-${index}`,
        className: 'crumb-current',
        title: (
          <Typography variant="body1" className="crumb-label">
            {item.label}
          </Typography>
        )
      };
    }

    return {
      key: `${item.label}-${index}`,
      href: item.href,
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (!item.onClick) {
          return;
        }

        const isModifiedClick =
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey;

        if (isModifiedClick) {
          return;
        }

        event.preventDefault();
        item.onClick();
      },
      title: (
        <Typography
          variant="body1"
          color={theme.dime.colors.text.secondary}
          className="crumb-label"
        >
          {item.label}
        </Typography>
      )
    };
  });

  return (
    <StyledBreadcrumb
      className={className}
      items={antItems}
      data-testid={testId ?? 'breadcrumbs'}
      {...antProps}
    />
  );
};
